/**
 * PINK #1 (P1) — pantalla del ADMIN para el piso (Configuración → Usuarios):
 *  - PINs de meseros (asignar / quitar; 4 dígitos, único por tenant).
 *  - Acceso de la TABLET compartida: crear/rotar el usuario "piso" (rol Mesero
 *    fijo) — la contraseña generada se muestra UNA sola vez — y "cerrar sesión
 *    de la tablet" (revoca sus refresh tokens; la tablet pide login de nuevo).
 * El PIN identifica, NO autoriza. Errores inline, sin window.alert.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { BUILD_ID } from '../buildInfo';

interface WaiterRow { id: string; name: string; email: string; hasPin: boolean; isFloor: boolean; onBreak?: boolean }
interface SessionRow { id: string; name: string; createdAt: string; lastSeenAt: string; revoked: boolean }

interface TableRow { id: string; name: string; assignedUserId?: string | null; assignedUserName?: string | null }

export function FloorAdminSection() {
  const [rows, setRows] = useState<WaiterRow[]>([]);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [tables, setTables] = useState<TableRow[]>([]);
  const [idleSecs, setIdleSecs] = useState<number>(180);
  const [tableMode, setTableMode] = useState<'open' | 'owner'>('open');
  const [breakHandoff, setBreakHandoff] = useState<'anyone' | 'admin'>('anyone');
  const [err, setErr] = useState<string | null>(null);
  const [pinFor, setPinFor] = useState<string | null>(null); // userId editando PIN
  const [pinVal, setPinVal] = useState('');
  const [busy, setBusy] = useState(false);
  const [tabletCred, setTabletCred] = useState<{ email: string; password: string; rotated: boolean } | null>(null);
  const [revokeMsg, setRevokeMsg] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);

  const load = useCallback(() => {
    api('/pos/floor/waiters').then((r) => setRows(r as WaiterRow[])).catch(() => setRows([]));
    api('/pos/floor/sessions').then((r) => setSessions(r as SessionRow[])).catch(() => setSessions([]));
    api('/pos/tables').then((r) => setTables(r as TableRow[])).catch(() => setTables([]));
    api('/pos/floor/settings').then((r: any) => {
      setIdleSecs(Number(r?.lockIdleSeconds) || 180);
      setTableMode(r?.tableMode === 'owner' ? 'owner' : 'open');
      setBreakHandoff(r?.breakHandoff === 'admin' ? 'admin' : 'anyone');
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // P2 — asigna un mesero a una mesa (o la libera). Recarga el tablero.
  const assignTable = async (tableId: string, userId: string | null) => {
    setBusy(true); setErr(null);
    try {
      await api(`/pos/floor/assignments/${tableId}`, { method: 'PUT', body: { userId } });
      load();
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo asignar la mesa');
    } finally { setBusy(false); }
  };

  // P3 — pone/quita el descanso de un mesero (el admin puede el de cualquiera).
  const toggleBreak = async (userId: string, onBreak: boolean) => {
    setBusy(true); setErr(null);
    try {
      await api('/pos/floor/break', { method: 'PUT', body: { userId, onBreak } });
      load();
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo cambiar el descanso');
    } finally { setBusy(false); }
  };

  const saveBreakHandoff = async (mode: 'anyone' | 'admin') => {
    setBusy(true); setErr(null); setBreakHandoff(mode);
    try {
      await api('/pos/floor/settings', { method: 'PATCH', body: { breakHandoff: mode } });
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo cambiar el modo de descanso');
    } finally { setBusy(false); }
  };

  const saveTableMode = async (mode: 'open' | 'owner') => {
    setBusy(true); setErr(null); setTableMode(mode);
    try {
      await api('/pos/floor/settings', { method: 'PATCH', body: { tableMode: mode } });
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo cambiar el modo de mesas');
    } finally { setBusy(false); }
  };

  const savePin = async (userId: string, pin: string | null) => {
    setBusy(true); setErr(null);
    try {
      await api(`/pos/floor/users/${userId}/pin`, { method: 'PATCH', body: { pin } });
      setPinFor(null); setPinVal(''); load();
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo guardar el PIN');
    } finally { setBusy(false); }
  };

  const createTablet = async () => {
    setBusy(true); setErr(null); setRevokeMsg(null);
    try {
      const r = (await api('/pos/floor/user', { method: 'POST' })) as any;
      setTabletCred({ email: r.email, password: r.password, rotated: !!r.rotated });
      load();
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo crear el acceso de tablet');
    } finally { setBusy(false); }
  };

  // TREN 1.3 — dos acciones DISTINTAS y así rotuladas:
  // "Desconectar esta tablet": solo esa sesión (las credenciales siguen valiendo).
  const disconnectSession = async (id: string) => {
    setBusy(true); setErr(null);
    try {
      await api(`/pos/floor/sessions/${id}`, { method: 'DELETE' });
      setRevokeMsg('Tablet desconectada: su siguiente acción pedirá login (≤10 s). Las credenciales siguen valiendo para reconectarla.');
      load();
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo desconectar la tablet');
    } finally { setBusy(false); }
  };

  // "Bloquear acceso de piso" (robo/extravío): TODAS las sesiones + contraseña nueva.
  const lockdown = async () => {
    setBusy(true); setErr(null);
    try {
      const r = (await api('/pos/floor/lockdown', { method: 'POST' })) as any;
      setTabletCred({ email: r.email, password: r.password, rotated: true });
      setRevokeMsg(`Acceso de piso BLOQUEADO: ${r.revokedSessions} tablet(s) desconectadas y contraseña rotada — la vieja ya no entra.`);
      load();
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo bloquear el acceso de piso');
    } finally { setBusy(false); }
  };

  const renameSession = async (id: string, name: string) => {
    setBusy(true); setErr(null);
    try {
      await api(`/pos/floor/sessions/${id}`, { method: 'PATCH', body: { name } });
      setRenaming(null); load();
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo renombrar');
    } finally { setBusy(false); }
  };

  const saveIdle = async (secs: number) => {
    setBusy(true); setErr(null);
    try {
      await api('/pos/floor/settings', { method: 'PATCH', body: { lockIdleSeconds: secs } });
      setIdleSecs(secs);
    } catch (e: any) {
      setErr(e?.body?.message || 'No se pudo guardar el tiempo de bloqueo');
    } finally { setBusy(false); }
  };

  const waiters = rows.filter((r) => !r.isFloor);
  const tablet = rows.find((r) => r.isFloor);

  return (
    <div className="rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">Piso y PINs (tablet compartida)</p>
        {/* Regla 11/09: paso 0 de todo smoke = confirmar el build en pantalla. */}
        <span className="text-[10px] text-slate-400">build {BUILD_ID}</span>
      </div>
      <p className="mt-2 text-sm text-slate-600">
        Cada mesero teclea su <strong>PIN de 4 dígitos</strong> al abrir una venta en la tablet compartida:
        la venta queda a su nombre y su corte «Mis tickets» cuadra. El PIN <strong>identifica, no autoriza</strong>.
      </p>
      {err && <p className="alert-over mt-3 rounded-xl px-3 py-2 text-xs">{err}</p>}

      <div className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-100">
        {waiters.map((w) => (
          <div key={w.id} className="flex flex-wrap items-center gap-2 px-3 py-2.5 text-sm">
            <span className="min-w-0 flex-1 truncate font-medium text-slate-800">
              {w.name}
              {/* P3 — chip de descanso + toggle (el admin lo prende/apaga). */}
              {w.onBreak && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-700">En descanso</span>}
            </span>
            {!w.isFloor && w.hasPin && (
              <button className="text-[11px] font-medium text-slate-500 underline decoration-dotted hover:text-slate-900"
                disabled={busy} onClick={() => toggleBreak(w.id, !w.onBreak)}>
                {w.onBreak ? 'Terminar descanso' : 'Poner en descanso'}
              </button>
            )}
            {pinFor === w.id ? (
              <span className="flex items-center gap-2">
                <input className="input !w-20 !py-1 text-center text-sm tracking-widest" inputMode="numeric"
                  maxLength={4} placeholder="0000" value={pinVal} autoFocus
                  onChange={(e) => setPinVal(e.target.value.replace(/\D/g, '').slice(0, 4))} />
                <button className="rounded-lg bg-slate-900 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-40"
                  disabled={busy || pinVal.length !== 4} onClick={() => savePin(w.id, pinVal)}>Guardar</button>
                <button className="text-xs text-slate-500 hover:text-slate-900" onClick={() => { setPinFor(null); setPinVal(''); setErr(null); }}>Cancelar</button>
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${w.hasPin ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {w.hasPin ? 'PIN asignado' : 'Sin PIN'}
                </span>
                <button className="text-xs font-medium text-slate-600 underline decoration-dotted hover:text-slate-900"
                  onClick={() => { setPinFor(w.id); setPinVal(''); setErr(null); }}>
                  {w.hasPin ? 'Cambiar' : 'Asignar PIN'}
                </button>
                {w.hasPin && (
                  <button className="text-xs text-slate-400 hover:text-rose-600" disabled={busy}
                    onClick={() => savePin(w.id, null)}>Quitar</button>
                )}
              </span>
            )}
          </div>
        ))}
        {waiters.length === 0 && (
          <p className="px-3 py-3 text-xs text-slate-400">Sin usuarios activos. Crea a tus meseros en «Usuarios» (rol Mesero).</p>
        )}
      </div>

      {/* P2 — asignación de mesas × mesero + modo. */}
      <div className="mt-5 rounded-xl bg-slate-50 p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mesas por mesero</p>
          <label className="flex items-center gap-2 text-[11px] text-slate-600">
            Modo
            <select className="input !w-auto !py-1 text-xs" value={tableMode} disabled={busy}
              onChange={(e) => saveTableMode(e.target.value as 'open' | 'owner')}>
              <option value="open">Cualquiera comanda (asignación informativa)</option>
              <option value="owner">Mesa única por mesero (abrir ajena pide su PIN)</option>
            </select>
          </label>
        </div>
        <p className="mb-2 text-[11px] text-slate-500">
          {tableMode === 'owner'
            ? 'Modo estricto: abrir una mesa asignada a OTRO exige el PIN del dueño o del encargado. Las mesas sin asignar quedan libres.'
            : 'La asignación colorea el tablero, pero cualquiera puede abrir cualquier mesa (con el aviso de mesa ajena).'}
        </p>
        {/* P3 — handoff del descanso (solo relevante en modo estricto). */}
        <label className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
          Cuando un mesero está en descanso, sus mesas
          <select className="input !w-auto !py-1 text-xs" value={breakHandoff} disabled={busy}
            onChange={(e) => saveBreakHandoff(e.target.value as 'anyone' | 'admin')}>
            <option value="anyone">se abren a todos (default)</option>
            <option value="admin">pasan al encargado</option>
          </select>
        </label>
        <div className="divide-y divide-slate-200/70 rounded-xl border border-slate-200 bg-white">
          {tables.map((t) => (
            <div key={t.id} className="flex items-center gap-2 px-3 py-2 text-xs">
              <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{t.name}</span>
              <select className="input !w-44 !py-1 text-xs" value={t.assignedUserId ?? ''} disabled={busy}
                onChange={(e) => assignTable(t.id, e.target.value || null)}>
                <option value="">— Libre —</option>
                {waiters.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          ))}
          {tables.length === 0 && (
            <p className="px-3 py-3 text-xs text-slate-400">Sin mesas. Créalas en GastroGest → Mesas.</p>
          )}
        </div>
      </div>

      <div className="mt-5 rounded-xl bg-slate-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Acceso de la tablet</p>
        <p className="mt-1 text-xs text-slate-500">
          Un solo usuario compartido con rol <strong>Mesero</strong> para la(s) tablet(s) del piso.
          La sesión dura 14 días y se renueva sola mientras se use.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
            disabled={busy} onClick={createTablet}>
            {tablet ? 'Regenerar contraseña de la tablet' : 'Crear acceso de tablet'}
          </button>
          {tablet && (
            <button className="rounded-xl border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-40"
              disabled={busy} onClick={lockdown}
              title="Robo/extravío: desconecta TODAS las tablets y rota la contraseña">
              Bloquear acceso de piso
            </button>
          )}
          <label className="ml-auto flex items-center gap-1 text-[11px] text-slate-500">
            Bloquear tras
            <input className="input !w-16 !py-1 text-center text-xs" type="number" min={10} max={600}
              value={idleSecs} disabled={busy}
              onChange={(e) => setIdleSecs(Number(e.target.value))}
              onBlur={(e) => { const v = Math.min(600, Math.max(10, Number(e.target.value) || 180)); void saveIdle(v); }} />
            s sin actividad
          </label>
        </div>

        {/* TREN 1.3 — tablets conectadas (una sesión por tablet, con último uso). */}
        {sessions.length > 0 && (
          <div className="mt-3 divide-y divide-slate-200/70 rounded-xl border border-slate-200 bg-white">
            {sessions.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs">
                {renaming?.id === s.id ? (
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    <input className="input !w-40 !py-1 text-xs" value={renaming.name} autoFocus
                      onChange={(e) => setRenaming({ id: s.id, name: e.target.value })} />
                    <button className="font-medium text-slate-700 underline" disabled={busy}
                      onClick={() => renameSession(s.id, renaming.name)}>Guardar</button>
                    <button className="text-slate-400" onClick={() => setRenaming(null)}>✕</button>
                  </span>
                ) : (
                  <button className="min-w-0 flex-1 truncate text-left font-medium text-slate-800 underline decoration-dotted"
                    title="Renombrar (p. ej. Tablet barra)" onClick={() => setRenaming({ id: s.id, name: s.name })}>
                    {s.name}
                  </button>
                )}
                <span className="text-slate-400">
                  último uso {new Date(s.lastSeenAt).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
                {s.revoked ? (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">desconectada</span>
                ) : (
                  <button className="rounded-lg border border-slate-300 px-2 py-0.5 font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                    disabled={busy} onClick={() => disconnectSession(s.id)}
                    title="Solo esta tablet; las credenciales siguen valiendo">
                    Desconectar esta tablet
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {tabletCred && (
          <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <p className="font-semibold">Apunta esto AHORA — no se vuelve a mostrar:</p>
            <p className="mt-1">Usuario: <code className="font-mono">{tabletCred.email}</code></p>
            <p>Contraseña: <code className="font-mono">{tabletCred.password}</code></p>
            {tabletCred.rotated && <p className="mt-1">La contraseña anterior dejó de servir.</p>}
          </div>
        )}
        {revokeMsg && <p className="mt-2 rounded-lg bg-white px-3 py-1.5 text-xs text-slate-600">{revokeMsg}</p>}
      </div>
    </div>
  );
}
